import { expressApi } from '@/state/api/expressApi';
import type {
  XtmCloudConfigResponse,
  XtmCloudCustomer,
  SetXtmCloudCredentialsRequest,
  SelectXtmCloudCustomerRequest,
  XtmCloudTemplate,
  SelectXtmCloudTemplateRequest,
  LlmConnector,
  LlmConfigResponse,
  SelectLlmConnectorRequest,
  SetLlmTranslationModeRequest,
  StyleGuideListResponse,
  SelectStyleGuideRequest,
  StyleGuideEditorConfig,
  StyleGuideEditorToken,
} from '@/types';

/**
 * The latest style guide selection started. A request that fails after a newer one began must not undo its own
 * optimistic patch: the undo would put the cache back to the value from before THAT request, over whatever the
 * newer click has since moved it to. The list is refetched after every selection (success or failure), so the
 * cache settles on what the server holds either way.
 */
let latestSelectionRequest = 0;

/**
 * The page drops its pick once a customer or template save succeeds and shows the cached config from then on. The save
 * also refetches the config, but until that read answers the cache would still hold the old value, so the old one
 * would flash back. Writing the saved value into the cache when the save succeeds closes that gap (the page reads the
 * cache itself, `currentData`, during the refetch); the refetch then brings the server's state, such as a template the
 * API cleared with the customer. A refused save changes nothing.
 */
async function writeSavedConfig(saving: Promise<unknown>, write: () => void): Promise<void> {
  try {
    await saving;
  } catch {
    return;
  }
  write();
}

/**
 * The previous customer's list says whether the API cleared the template with the customer: it clears one owned by
 * another customer and keeps a global one. A template the list does not know is left to the refetch.
 */
function moveToCustomer(
  draft: XtmCloudConfigResponse, customerId: SelectXtmCloudCustomerRequest['customerId'], listed: XtmCloudTemplate[] | undefined,
): void {
  draft.customerId = customerId;
  const template = listed?.find((one) => one.id === draft.templateId);
  if (template && !template.global) draft.templateId = null;
}

export const adminApi = expressApi.injectEndpoints({
  endpoints: (builder) => ({
    getXtmCloudConfig: builder.query<XtmCloudConfigResponse, void>({
      query: () => '/admin/xtm-cloud/config',
      providesTags: ['XtmCloudConfig'],
    }),
    getXtmCloudCustomers: builder.query<XtmCloudCustomer[], void>({
      query: () => '/admin/xtm-cloud/customers',
      providesTags: ['XtmCloudCustomers'],
    }),
    setXtmCloudCredentials: builder.mutation<void, SetXtmCloudCredentialsRequest>({
      query: (body) => ({ url: '/admin/xtm-cloud/credentials', method: 'PUT', body }),
      invalidatesTags: ['XtmCloudConfig', 'XtmCloudCustomers', 'XtmCloudTemplates'],
    }),
    // The template list is the default customer's, and the API clears a template the new customer cannot use.
    selectXtmCloudCustomer: builder.mutation<void, SelectXtmCloudCustomerRequest>({
      query: (body) => ({ url: '/admin/xtm-cloud/customer', method: 'PUT', body }),
      async onQueryStarted({ customerId }, { dispatch, getState, queryFulfilled }) {
        await writeSavedConfig(queryFulfilled, () => {
          const listed = adminApi.endpoints.getXtmCloudTemplates.select()(getState()).data;
          dispatch(adminApi.util.updateQueryData(
            'getXtmCloudConfig', undefined, (draft) => moveToCustomer(draft, customerId, listed),
          ));
        });
      },
      invalidatesTags: ['XtmCloudConfig', 'XtmCloudTemplates'],
    }),
    // COPA-925: the default customer's templates and the global ones. 409 until a default customer is saved.
    getXtmCloudTemplates: builder.query<XtmCloudTemplate[], void>({
      query: () => '/admin/xtm-cloud/templates',
      providesTags: ['XtmCloudTemplates'],
    }),
    selectXtmCloudTemplate: builder.mutation<void, SelectXtmCloudTemplateRequest>({
      query: (body) => ({ url: '/admin/xtm-cloud/template', method: 'PUT', body }),
      async onQueryStarted({ templateId }, { dispatch, queryFulfilled }) {
        await writeSavedConfig(queryFulfilled, () => dispatch(
          adminApi.util.updateQueryData('getXtmCloudConfig', undefined, (draft) => { draft.templateId = templateId; }),
        ));
      },
      invalidatesTags: ['XtmCloudConfig', 'XtmCloudTemplates'],
    }),
    getLlmConnectors: builder.query<LlmConnector[], void>({
      query: () => '/admin/llm/connectors',
      providesTags: ['LlmConnectors'],
    }),
    getLlmConfig: builder.query<LlmConfigResponse, void>({
      query: () => '/admin/llm/config',
      providesTags: ['LlmConfig'],
    }),
    // The saved connector is written into the cached config once the save succeeds, so the page does not show the old
    // (possibly missing) connector while the config is read again.
    selectLlmConnector: builder.mutation<void, SelectLlmConnectorRequest>({
      query: (body) => ({ url: '/admin/llm/connector', method: 'PUT', body }),
      async onQueryStarted({ connectorId }, { dispatch, queryFulfilled }) {
        await writeSavedConfig(queryFulfilled, () => dispatch(
          adminApi.util.updateQueryData('getLlmConfig', undefined, (draft) => { draft.connectorId = connectorId; }),
        ));
      },
      invalidatesTags: ['LlmConfig'],
    }),
    setLlmTranslationMode: builder.mutation<void, SetLlmTranslationModeRequest>({
      query: (body) => ({ url: '/admin/llm/translation-mode', method: 'PUT', body }),
      invalidatesTags: ['LlmConfig'],
    }),
    getStyleGuides: builder.query<StyleGuideListResponse, void>({
      query: () => '/admin/style-guides',
      providesTags: ['StyleGuides'],
    }),
    // One selection per organization (COPA-818). The choice moves at once and springs back if the save fails;
    // without this the click looked ignored until the PUT and the refetch had both answered. The list is
    // refetched either way (RTK Query invalidates on failure too), so a guide another administrator just
    // deleted drops out of the list after its 404.
    selectStyleGuide: builder.mutation<void, SelectStyleGuideRequest>({
      query: (body) => ({ url: '/admin/style-guides/selection', method: 'PUT', body }),
      async onQueryStarted({ styleGuideId }, { dispatch, queryFulfilled }) {
        const request = ++latestSelectionRequest;
        const patch = dispatch(
          adminApi.util.updateQueryData('getStyleGuides', undefined, (draft) => {
            const chosen = draft.styleGuides.find((one) => one.id === styleGuideId);
            draft.selection = {
              configured: true,
              styleGuideId,
              styleGuideName: chosen?.name ?? null,
              missingSince: null,
            };
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          if (request === latestSelectionRequest) patch.undo();
        }
      },
      invalidatesTags: ['StyleGuides'],
    }),
    getStyleGuideEditor: builder.query<StyleGuideEditorConfig, void>({
      query: () => '/admin/style-guides/editor',
      keepUnusedDataFor: Infinity, // cached for the session, as the editor settings do not change under it
    }),
    // A mutation so every call reaches the server: the form asks again after a 401, and a cached token would
    // outlive the access it was issued for. Not exported as a hook: a tracked mutation result lands in the
    // Redux store, token included. Call it through useStyleGuideEditorToken, which dispatches with track: false.
    issueStyleGuideEditorToken: builder.mutation<StyleGuideEditorToken, { styleGuideId: string }>({
      query: (body) => ({ url: '/admin/style-guides/editor-token', method: 'POST', body }),
      // A 403 here means the admin role was revoked mid-edit. The editor handles it (read-only, edits kept), so it
      // must not also send the whole app to the access-denied page.
      extraOptions: { skipForbiddenRedirect: true },
    }),
    deleteStyleGuide: builder.mutation<void, string>({
      query: (styleGuideId) => ({
        url: `/admin/style-guides/${encodeURIComponent(styleGuideId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['StyleGuides'],
    }),
  }),
});

export const {
  useGetXtmCloudConfigQuery,
  useGetXtmCloudCustomersQuery,
  useSetXtmCloudCredentialsMutation,
  useSelectXtmCloudCustomerMutation,
  useGetXtmCloudTemplatesQuery,
  useSelectXtmCloudTemplateMutation,
  useGetLlmConnectorsQuery,
  useGetLlmConfigQuery,
  useSelectLlmConnectorMutation,
  useSetLlmTranslationModeMutation,
  useGetStyleGuidesQuery,
  useSelectStyleGuideMutation,
  useGetStyleGuideEditorQuery,
  useDeleteStyleGuideMutation,
} = adminApi;
